/**
 * Utility functions for formatting dates in the Review Page
 */

export interface DateType {
    month?: string | null;
    year?: number | null;
}

export interface DateRangeType {
    fromDate?: DateType | null;
    toDate?: DateType | null;
    isCurrent?: boolean | null;
}

/**
 * Convert JobSeekerProfileMonthType enum to zero-based month index
 */
export const monthEnumToIndex = (month: string): number => {
    const monthMap: { [key: string]: number } = {
        JANUARY: 0,
        FEBRUARY: 1,
        MARCH: 2,
        APRIL: 3,
        MAY: 4,
        JUNE: 5,
        JULY: 6,
        AUGUST: 7,
        SEPTEMBER: 8,
        OCTOBER: 9,
        NOVEMBER: 10,
        DECEMBER: 11,
    };
    return monthMap[month] ?? 0;
};

/**
 * Format a single date (month + year) using Intl.DateTimeFormat
 */
export const formatDate = (date: DateType | null | undefined, locale: string): string => {
    if (!date) return '';

    // If we have both month and year, format as "Month Year"
    if (date.month && date.year) {
        const monthIndex = monthEnumToIndex(date.month);
        const dateObj = new Date(date.year, monthIndex, 1);
        return new Intl.DateTimeFormat(locale, {
            month: 'long',
            year: 'numeric',
        }).format(dateObj);
    }

    // If we only have year, format as "Year"
    if (date.year) {
        return date.year.toString();
    }

    return '';
};

/**
 * Format a date range for display, handling all possible combinations of optional fields
 *
 * @param dateRange - The date range object from GraphQL
 * @param locale - The locale for formatting (e.g., 'en-US', 'ja-JP')
 * @param presentText - The text to display for current/ongoing items (e.g., 'Present')
 * @param delimiter - The delimiter between from and to dates (default: ' – ')
 * @returns Formatted date range string or empty string if no valid dates
 *
 * @example
 * // Complete range
 * formatDateRangeDisplay({ fromDate: { month: 'JANUARY', year: 2020 }, toDate: { month: 'MARCH', year: 2022 } }, 'en-US', 'Present')
 * // Returns: "January 2020 – March 2022"
 *
 * @example
 * // Current position
 * formatDateRangeDisplay({ fromDate: { month: 'JANUARY', year: 2020 }, isCurrent: true }, 'en-US', 'Present')
 * // Returns: "January 2020 – Present"
 *
 * @example
 * // Only years
 * formatDateRangeDisplay({ fromDate: { year: 2020 }, toDate: { year: 2022 } }, 'en-US', 'Present')
 * // Returns: "2020 – 2022"
 *
 * @example
 * // Only fromDate
 * formatDateRangeDisplay({ fromDate: { month: 'JANUARY', year: 2020 } }, 'en-US', 'Present')
 * // Returns: "January 2020"
 */
export const formatDateRangeDisplay = (
    dateRange: DateRangeType | null | undefined,
    locale: string,
    presentText: string,
    delimiter: string = ' – ',
): string => {
    if (!dateRange) return '';

    const fromDateStr = formatDate(dateRange.fromDate, locale);
    const toDateStr = dateRange.isCurrent ? presentText : formatDate(dateRange.toDate, locale);

    // If we have both from and to dates, show the range
    if (fromDateStr && toDateStr) {
        return `${fromDateStr}${delimiter}${toDateStr}`;
    }

    // If we only have fromDate, show just that
    if (fromDateStr) {
        return fromDateStr;
    }

    // If we only have toDate (unusual but possible), show just that
    if (toDateStr) {
        return toDateStr;
    }

    return '';
};
